import React, { useMemo, useState } from 'react';
import { connect, useIntl, useDispatch, getLocale, useLocation } from 'umi';
import {
  Layout,
  Justify,
  Segment,
  Row,
  Col,
} from "tea-component";
import moment from 'moment';
import BreakdownContext from '@/pages/dashboard/pages/overview/components/BreakdownContext';
import CountriesContext from '@/pages/dashboard/pages/overview/components/CountriesContext';
import MonthFilterNew from '@/pages/dashboard/filterPageComponents/overview/monthlyFilterNew';
import { DATE_DIMENSION, FORM_MAP } from '@/pages/dashboard/pages/overview/_config/constant';
import KeyPerformanceMetrics from '@/pages/dashboard/pages/overview/components/KeyPerformanceMetrics';
import useMonthRegionModel from '@/pages/dashboard/servicesModels/overview/monthRegionModel';
import useMonthMetricsModel from '@/pages/dashboard/servicesModels/overview/monthMetricsUserModel';
import useEventData from '@/pages/dashboard/servicesModels/overview/overviewEventDataModel';
import OverviewTable from '@/pages/dashboard/pages/overview/components/OverviewTable';
import EngagementRevenueMetrics from '@/pages/dashboard/pages/overview/components/EngagementRevenueMetrics/EngagementRevenueMetrics';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { useBuriedPoint } from "@/pages/home/utils/customHook";
import MarqueeInfo from "@/pages/dashboard/components/MarqueeInfo";
import { getPageId, getReqType } from '@/pages/dashboard/pages/overview/_config/overviewMonthlyRequest';

import "@/pages/dashboard/assets/scss/page/Overlay/index.scss";
import { SubTitle } from '@/pages/dashboard/components/SubTitle';

const { Content } = Layout;
const Month = (props: any) => {
  const { gameCode, explainInfo, gameConfig, timeZone } = props;

  const location = useLocation();
  const regex = /\/overview\/([^/]+)/;
  const match = location.pathname.match(regex);
  const urlReqType = match ? match[1] : "monthly";
  const overviewMonthlyReqType = getReqType(urlReqType);

  const dispatch = useDispatch();
  const { formatMessage } = useIntl(); // 中英切换方法
  const { getLocalText } = useLocal();
  const isEN = getLocale() === 'en-US';
  const [formValue, setFormValue] = useState(
    {
      filterValue: null,
      newFilter: true,
      timeType: 'Month',
      [FORM_MAP.RANGE_TIME_DIFF]: true
    }
  );
  const { rangeTime } = formValue?.filterValue ?? [];
  const [filterLoading, setFilterLoading] = useState(true);
  const [regionDataType, setRegionDataType] = useState('country');

  const regionData = useMonthRegionModel(
    props,
    formValue,
    dispatch,
    filterLoading,
    regionDataType,
    overviewMonthlyReqType,
  );
  const metricsData = useMonthMetricsModel(
    props,
    formValue,
    dispatch,
    filterLoading,
    regionDataType,
    overviewMonthlyReqType,
  );
  const [eventData, loading] = useEventData(gameCode, formValue, filterLoading);

  // Countries / Area Breakdown
  const COUNTRY_AREA_ZONE = [
    { text: formatMessage({ id: 'overview_area_tab_country' }), value: 'country' },
    { text: formatMessage({ id: 'overview_area_tab_region' }), value: 'region' },
    { text: formatMessage({ id: 'overview_area_tab_zone' }), value: 'zone' },
    { text: formatMessage({ id: 'common_Channel' }), value: 'channel' },
    { text: formatMessage({ id: 'overview_area_tab_os' }), value: 'os' },
  ];

  const pageId = getPageId(overviewMonthlyReqType);

  // 打点
  const logObj = {
    pageId,
    pageName: overviewMonthlyReqType === "xh" ? 'overviewMonthlyXh' : 'overviewMonthly',
    extInfo: overviewMonthlyReqType === "xh" ? 'overviewMonthlyXh' : 'overviewMonthly',
    pagePath: `/overview/${urlReqType}`,
    uid: gameCode,
    typeId: 'dashboard_pc_v2',
  };

  useBuriedPoint(logObj);


  const handleConfirm = (value: any) => {
    setFormValue({
      ...formValue,
      ...value
      // [FORM_MAP.IS_INIT]: value.rangeInit
    });
  };

  const handleInit = (value: any) => {
    setFormValue({
      ...formValue,
      ...value,
    });
    setFilterLoading(false);
  };

  // Countries / Area Breakdown模块change方法；
  const changeBreakdown = useMemo(() => (
    (val) => {
      setRegionDataType(val);
    }
  ), [formValue, gameCode, filterLoading]);

  const dateStr: string = useMemo(() => {
    const start = rangeTime?.[0];
    // 筛选器传出的endDate为1号，这里转一下为对应月的最后一天
    const end = (moment(rangeTime?.[1], 'YYYY-MM-DD'))?.endOf('month')?.format('YYYY-MM-DD');
    return rangeTime?.length > 1 ? `${start} ${end && (end !== start) ? ` ~ ${end}` : ""}` : '';
  }, [formValue]);

  return (
    <Content className='dbd-scroll-body'>
      <Content.Header
        title={<div className='page-title'>{formatMessage({ id: 'overview_monthly_Monthly' })}{overviewMonthlyReqType === 'xh' && '-去小号'}</div>}
        subtitle={<SubTitle/>}
      ></Content.Header>
      <Content.Body full>
        <MarqueeInfo pagePath={"/overview/monthly"} />
        <MonthFilterNew
          openSticky={true}
          filterConfirm={handleConfirm}
          filterReset={handleConfirm}
          timeZone={timeZone}
          buttonLogConfig={{
            pageId: logObj.pageId, confirmButtonId: `${logObj.pageId}001`,
            resetButtonId: `${logObj.pageId}002`
          }}
          filterLoading={filterLoading}
          setFilterLoading={setFilterLoading}
          filterInit={handleInit}
        />

        <Justify
          className="overlay-margin"
          left={
            <div className='overlay-title'>{getLocalText('overview_Key_Performance_Metrics')}</div>
          }
          right={
            <div className='overlay-time'>{dateStr}</div>
          }
        />
        <KeyPerformanceMetrics
          parentProps={props}
          formValue={formValue}
          dispatch={dispatch}
          filterLoading={filterLoading}
          gameCode={gameCode}
          explainInfo={explainInfo}
          dateDimension={DATE_DIMENSION.MONTH}
          overviewMonthlyReqType={overviewMonthlyReqType}
        />
        <Row className="margin-t-10">
          <Col>
            <EngagementRevenueMetrics
              renderIndex={0}
              eventData={eventData}
              loading={loading}
              gameCode={gameCode}
              filterLoading={filterLoading}
              formValue={formValue}
              explainInfo={explainInfo}
              type={DATE_DIMENSION.MONTH}
              dailyReqType={overviewMonthlyReqType}
            />
          </Col>
          <Col>
            <EngagementRevenueMetrics
              renderIndex={1}
              eventData={eventData}
              loading={loading}
              gameCode={gameCode}
              filterLoading={filterLoading}
              formValue={formValue}
              explainInfo={explainInfo}
              type={DATE_DIMENSION.MONTH}
              dailyReqType={overviewMonthlyReqType}
            />
          </Col>
        </Row>
        <Row className="margin-t-10">
          <Col>
            <OverviewTable
              filterValue={formValue}
              gameConfig={gameConfig}
              tableType='month'
              filterLoading={filterLoading}
              gameCode={gameCode}
              explainInfo={explainInfo}
              dailyReqType={overviewMonthlyReqType}
              logObj={logObj}
            />
          </Col>
        </Row>
        <Row className="margin-t-10">
          <Col>
            <BreakdownContext
              componetIndex={0}
              data={metricsData}
              gameCode={gameCode}
              type={DATE_DIMENSION.MONTH}
              formValue={formValue}
            />
          </Col>

          <Col>
            <BreakdownContext
              componetIndex={1}
              data={metricsData}
              gameCode={gameCode}
              type={DATE_DIMENSION.MONTH}
              formValue={formValue}
            />
          </Col>
        </Row>
        <Justify className="overlay-margin-breakdown" left={
          <Segment
            className={isEN ? "segment-light" : "segment-light segment-light-china"}
            options={COUNTRY_AREA_ZONE}
            defaultValue={'country'}
            onChange={changeBreakdown}
          />
        } />
        {/* 只有三个，没必要map */}
        <Row>
          <Col className='margin-b-10'>
            <CountriesContext
              index={0}
              regionDataType={regionDataType}
              data={regionData}
              formValue={formValue}
              activeUserType="MAU"
              type="DAU"
              gameCode={gameCode}
            />
          </Col>
        </Row>
        <Row>
          <Col className='margin-b-10'>
            <CountriesContext
              index={1}
              regionDataType={regionDataType}
              data={regionData}
              formValue={formValue}
              activeUserType="MAU"
              type="Revenue"
              gameCode={gameCode}
            />
          </Col>
        </Row>

        <Row>
          <Col className='margin-b-10'>
            <CountriesContext
              index={2}
              regionDataType={regionDataType}
              data={regionData}
              formValue={formValue}
              activeUserType="MAU"
              type="New"
              gameCode={gameCode}
            />
          </Col>
        </Row>
      </Content.Body>
    </Content >
  );
};
export default connect((state: any) => ({
  serviceData: state.overview?.data,
  gameCode: state.dashboard?.currentGame?.game_code,
  explainInfo: state.dashboard?.explainInfo,
  gameConfig: state?.dashboardSetting?.gameConfig
}))(Month);
